import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import caseStudyService from '../services/caseStudyService';

const useCaseStudies = () => {
  const [caseStudies, setCaseStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const fetchCaseStudies = useCallback(async (searchQuery = '') => {
    setLoading(true);
    try {
      const data = await caseStudyService.getCaseStudies(searchQuery);
      setCaseStudies(data);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to load case studies');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchCaseStudies(search);
    }, 300);
    return () => clearTimeout(handler);
  }, [search, fetchCaseStudies]);

  const addCaseStudy = async (data) => {
    try {
      await caseStudyService.createCaseStudy(data);
      toast.success('Case study created successfully');
      fetchCaseStudies(search);
      return true;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create case study');
      return false;
    }
  };

  const updateCaseStudy = async (id, data) => {
    try {
      await caseStudyService.updateCaseStudy(id, data);
      toast.success('Case study updated successfully');
      fetchCaseStudies(search);
      return true;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update case study');
      return false;
    }
  };

  const deleteCaseStudy = async (id) => {
    try {
      await caseStudyService.deleteCaseStudy(id);
      toast.success('Case study deleted successfully');
      fetchCaseStudies(search);
      return true;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete case study');
      return false;
    }
  };

  const reorderCaseStudies = async (reorderedItems) => {
    try {
      setCaseStudies(reorderedItems);
      const payload = reorderedItems.map((item, index) => ({
        id: item.id,
        priority: index + 1
      }));
      await caseStudyService.reorderCaseStudies(payload);
      toast.success('Case studies reordered successfully');
      return true;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to reorder case studies');
      fetchCaseStudies(search);
      return false;
    }
  };

  return {
    caseStudies,
    loading,
    addCaseStudy,
    updateCaseStudy,
    deleteCaseStudy,
    reorderCaseStudies,
    search,
    setSearch,
    refresh: fetchCaseStudies,
  };
};

export default useCaseStudies;
